import { parseFloatSafe } from '@amazon/vinyl-util'

export interface Rgb {
    r: number // 0–255
    g: number // 0–255
    b: number // 0–255
    a: number // 0-1
}

export interface Hsl {
    h: number // 0–360
    s: number // 0–1
    l: number // 0–1
    a: number // 0–1
}

/**
 * Convert Rgb → Hsl.
 *
 * @param rgb The input Rgb to convert.
 * @param out Optionally provide the output Hsl to mutate. Creates a new object otherwise.
 */
export function rgbToHsl({ r, g, b, a }: Rgb, out?: Hsl): Hsl {
    const rNorm = r / 255
    const gNorm = g / 255
    const bNorm = b / 255

    const max = Math.max(rNorm, gNorm, bNorm)
    const min = Math.min(rNorm, gNorm, bNorm)
    const d = max - min

    let h = 0
    let s = 0
    const l = (max + min) / 2

    if (d !== 0) {
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min)

        switch (max) {
            case rNorm:
                h = (gNorm - bNorm) / d + (gNorm < bNorm ? 6 : 0)
                break
            case gNorm:
                h = (bNorm - rNorm) / d + 2
                break
            case bNorm:
                h = (rNorm - gNorm) / d + 4
                break
        }
        h *= 60
    }

    const result = out || ({} as Hsl)
    result.h = h
    result.s = s
    result.l = l
    result.a = a
    return result
}

/**
 * Convert Hsl → Rgb.
 *
 * @param hsl The input Hsl to convert.
 * @param out Optionally provide the output Rgb to mutate. Creates a new object otherwise.
 */
export function hslToRgb({ h, s, l, a }: Hsl, out?: Rgb): Rgb {
    const hue2rgb = (p: number, q: number, t: number) => {
        if (t < 0) t += 1
        if (t > 1) t -= 1
        if (t < 1 / 6) return p + (q - p) * 6 * t
        if (t < 1 / 2) return q
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
        return p
    }

    let r: number, g: number, b: number

    if (s === 0) {
        r = g = b = l // achromatic
    } else {
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s
        const p = 2 * l - q
        const hk = h / 360
        r = hue2rgb(p, q, hk + 1 / 3)
        g = hue2rgb(p, q, hk)
        b = hue2rgb(p, q, hk - 1 / 3)
    }

    const result = out || ({} as Rgb)
    result.r = Math.round(r * 255)
    result.g = Math.round(g * 255)
    result.b = Math.round(b * 255)
    result.a = a
    return result
}

/**
 * Parses a string representation of a color.
 *
 * Supports #rrggbb, #rrggbbaa, #rgb, #rgba, rgb(...), rgba(...), hsl(...), hsla(...).
 */
export function parseColor(input: string): Rgb | null {
    const color = input.trim().toLowerCase()

    // #rgb, #rgba, #rrggbb, #rrggbbaa
    if (color.startsWith('#')) {
        const hex = color.slice(1)
        if (hex.length === 3) {
            const [r, g, b] = hex.split('').map((x) => parseInt(x + x, 16))
            return { r, g, b, a: 1 }
        } else if (hex.length === 4) {
            const [r, g, b, a] = hex.split('').map((x) => parseInt(x + x, 16))
            return { r, g, b, a: a / 255 }
        } else if (hex.length === 6) {
            const r = parseInt(hex.slice(0, 2), 16)
            const g = parseInt(hex.slice(2, 4), 16)
            const b = parseInt(hex.slice(4, 6), 16)
            return { r, g, b, a: 1 }
        } else if (hex.length === 8) {
            const r = parseInt(hex.slice(0, 2), 16)
            const g = parseInt(hex.slice(2, 4), 16)
            const b = parseInt(hex.slice(4, 6), 16)
            const a = parseInt(hex.slice(6, 8), 16) / 255
            return { r, g, b, a }
        }
    }

    // rgb() or rgba()
    const rgbMatch = color.match(/^rgba?\(([^)]+)\)$/)
    if (rgbMatch) {
        const parts = rgbMatch[1].split(',').map((p) => p.trim())
        const r = parseInt(parts[0], 10)
        const g = parseInt(parts[1], 10)
        const b = parseInt(parts[2], 10)
        const a = parseFloatSafe(parts[3]) ?? 1
        return { r, g, b, a }
    }

    // hsl() or hsla()
    const hslMatch = color.match(/^hsla?\(([^)]+)\)$/)
    if (hslMatch) {
        const parts = hslMatch[1].split(',').map((p) => p.trim())
        const h = parseFloat(parts[0])
        const s = parseFloat(parts[1]) / 100
        const l = parseFloat(parts[2]) / 100
        const a = parseFloatSafe(parts[3]) ?? 1
        return hslToRgb({ h, s, l, a })
    }

    return null
}

/** Convert RGBA object → CSS rgba() string. */
export function rgbaToCssString({ r, g, b, a }: Rgb): string {
    const rr = Math.round(r)
    const gg = Math.round(g)
    const bb = Math.round(b)
    const aa = +a.toFixed(3)
    return `rgba(${rr}, ${gg}, ${bb}, ${aa})`
}

/** Convert RGBA object → hex string (#rrggbb or #rrggbbaa). */
export function rgbToHex({ r, g, b, a }: Rgb): string {
    const toHex = (n: number) => n.toString(16).padStart(2, '0')
    const rr = toHex(Math.round(r))
    const gg = toHex(Math.round(g))
    const bb = toHex(Math.round(b))
    if (a >= 1) return `#${rr}${gg}${bb}`
    const aa = toHex(Math.round(a * 255))
    return `#${rr}${gg}${bb}${aa}`
}

export function rgbToCss({ r, g, b }: Rgb): string {
    return `rgb(${r}, ${g}, ${b})`
}

export function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max)
}

/** Perceptual OKLCH: `l` 0–1, `c` 0–~0.4 chroma, `h` 0–360 hue degrees. */
export interface Oklch {
    l: number
    c: number
    h: number
}

// sRGB companding on 0–1 channels.
function srgbToLinear(x: number): number {
    return x <= 0.04045 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4
}
function linearToSrgb(x: number): number {
    return x <= 0.0031308 ? 12.92 * x : 1.055 * Math.max(x, 0) ** (1 / 2.4) - 0.055
}

function rgbToXyz([r, g, b]: [number, number, number]) {
    const rl = srgbToLinear(r)
    const gl = srgbToLinear(g)
    const bl = srgbToLinear(b)
    return {
        x: 0.4124564 * rl + 0.3575761 * gl + 0.1804375 * bl,
        y: 0.2126729 * rl + 0.7151522 * gl + 0.072175 * bl,
        z: 0.0193339 * rl + 0.119192 * gl + 0.9503041 * bl,
    }
}

function xyzToRgb({ x, y, z }: { x: number; y: number; z: number }): [number, number, number] {
    const r = 3.2404542 * x - 1.5371385 * y - 0.4985314 * z
    const g = -0.969266 * x + 1.8760108 * y + 0.041556 * z
    const b = 0.0556434 * x - 0.2040259 * y + 1.0572252 * z
    return [linearToSrgb(r), linearToSrgb(g), linearToSrgb(b)]
}

function xyzToOklab({ x, y, z }: { x: number; y: number; z: number }) {
    const l = Math.cbrt(0.819022437996703 * x + 0.3619062600528904 * y - 0.1288737815209879 * z)
    const m = Math.cbrt(0.0329836539323885 * x + 0.9292868615863434 * y + 0.0361446663506424 * z)
    const s = Math.cbrt(0.0481771893596242 * x + 0.2642395317527308 * y + 0.6335478284694309 * z)
    return {
        l: 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
        a: 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
        b: 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
    }
}

function oklabToXyz({ l, a, b }: { l: number; a: number; b: number }) {
    const l3 = (l + 0.3963377774 * a + 0.2158037573 * b) ** 3
    const m3 = (l - 0.1055613458 * a - 0.0638541728 * b) ** 3
    const s3 = (l - 0.0894841775 * a - 1.291485548 * b) ** 3
    return {
        x: 1.2268798734 * l3 - 0.5578149966 * m3 + 0.2813910502 * s3,
        y: -0.0405757626 * l3 + 1.1122868294 * m3 - 0.0717110667 * s3,
        z: -0.0763729497 * l3 - 0.4214933239 * m3 + 1.5869240244 * s3,
    }
}

export function rgbToOklch({ r, g, b }: Rgb): Oklch {
    const lab = xyzToOklab(rgbToXyz([r / 255, g / 255, b / 255]))
    const h = (Math.atan2(lab.b, lab.a) * 180) / Math.PI
    return { l: lab.l, c: Math.hypot(lab.a, lab.b), h: (h + 360) % 360 }
}

/** OKLCH → sRGB. Out-of-gamut channels are clamped into 0–255. */
export function oklchToRgb({ l, c, h }: Oklch): Rgb {
    const hr = (h * Math.PI) / 180
    const [r, g, b] = xyzToRgb(oklabToXyz({ l, a: c * Math.cos(hr), b: c * Math.sin(hr) }))
    const ch = (x: number) => Math.round(clamp(x, 0, 1) * 255)
    return { r: ch(r), g: ch(g), b: ch(b), a: 1 }
}

function relativeLuminance({ r, g, b }: Rgb): number {
    const [rl, gl, bl] = [r, g, b].map((c) => srgbToLinear(c / 255))
    return 0.2126 * rl + 0.7152 * gl + 0.0722 * bl
}

/** WCAG contrast ratio between two colors (1 = identical, 21 = black/white). */
export function contrastRatio(a: Rgb, b: Rgb): number {
    const la = relativeLuminance(a)
    const lb = relativeLuminance(b)
    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}

/**
 * Nudge `fg`'s OKLCH lightness away from `bg` until it clears `target` WCAG
 * contrast, or give up after a bounded search and return the closest attempt.
 */
export function ensureContrast(fg: Oklch, bg: Rgb, target: number): Oklch {
    if (contrastRatio(oklchToRgb(fg), bg) >= target) return fg
    const dir = rgbToOklch(bg).l > 0.5 ? -1 : 1
    const out = { ...fg }
    for (let i = 0; i < 30; i++) {
        out.l = clamp(out.l + dir * 0.02, 0, 1)
        if (contrastRatio(oklchToRgb(out), bg) >= target) return out
    }
    return out
}
